import React, { useEffect, useRef, useState } from "react";
import {
  Modal,
  Input,
  Button,
  Form,
  Radio,
  Table,
  Checkbox,
  Popover,
  InputRef,
  Alert,
} from "antd";
import Header from "./Header";
import { useRolesData } from "./context/RolesContext";
import { ManageUserFormData, Role } from "./types";
import InputMask from "react-input-mask";

interface ManageUserModalProps {
  user: any;
  visible: boolean;
  existingEmails: string[];
  existingPhones: string[];
  onClose: () => void;
  onSaveUser: (updatedUser: any) => void;
}

interface ManageUserFormProps {
  formData: ManageUserFormData;
  existingEmails: string[];
  existingPhones: string[];
  onFormDataChange: (data: Partial<ManageUserFormData>) => void;
  errors: Partial<ManageUserFormData>;
  setErrors: React.Dispatch<React.SetStateAction<Partial<ManageUserFormData>>>;
}

const formatPhoneNumber = (value: string) => {
  const cleaned = value.replace(/\D/g, "").slice(0, 10);
  return cleaned;
};

const ManageUserForm: React.FC<ManageUserFormProps> = ({
  formData,
  existingEmails,
  existingPhones,
  onFormDataChange,
  errors,
  setErrors,
}) => {
  const {
    first_name: first_name,
    last_name: lastName,
    email,
    phone_number: phone,
    status,
  } = formData;

  const firstNameRef = useRef<InputRef>(null);
  const lastNameRef = useRef<InputRef>(null);
  const emailRef = useRef<InputRef>(null);
  const phoneRef = useRef<InputRef>(null);

  useEffect(() => {
    firstNameRef.current?.focus();
  }, []);

  const isEmailValid = (email: string) => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const isPhoneValid = (phone: string) => {
    const regex = /^\d{10}$/;
    return regex.test(phone);
  };

  const handleInputChange = (key: keyof ManageUserFormData, value: string) => {
    onFormDataChange({ [key]: value });

    if (key === "email") {
      if (!isEmailValid(value)) {
        setErrors((prev) => ({ ...prev, email: "Invalid email format" }));
      } else if (
        existingEmails.some(
          (existingEmail) => existingEmail.toLowerCase() === value.toLowerCase()
        )
      ) {
        setErrors((prev) => ({
          ...prev,
          email: "A user with the same email already exists",
        }));
      } else {
        setErrors((prev) => ({ ...prev, email: undefined }));
      }
    }

    if (key === "phone_number") {
      if (!isPhoneValid(value)) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "Invalid phone number",
        }));
      } else if (existingPhones.includes(value)) {
        setErrors((prev) => ({
          ...prev,
          phone_number: "A user with the same phone number already exists",
        }));
      } else {
        setErrors((prev) => ({ ...prev, phone_number: undefined }));
      }
    }

    if (key === "first_name") {
      if (!value) {
        setErrors((prev) => ({
          ...prev,
          first_name: "First name is required",
        }));
      } else {
        setErrors((prev) => ({ ...prev, first_name: undefined }));
      }
    }

    if (key === "last_name") {
      if (!value) {
        setErrors((prev) => ({ ...prev, last_name: "Last name is required" }));
      } else {
        setErrors((prev) => ({ ...prev, last_name: undefined }));
      }
    }
  };

  const handleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    nextRef: React.RefObject<InputRef>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      nextRef.current?.focus();
    }
    if (e.key === "Tab") {
      e.preventDefault();
      nextRef.current?.focus();
    }
  };

  const handleStatusChange = (e: any) => {
    onFormDataChange({ status: e.target.value });
  };

  const options = [
    { label: "Active", value: "active" },
    { label: "Inactive", value: "inactive" },
    { label: "Forgot Password", value: "forgot-password" },
  ];

  return (
    <div style={{ margin: "0px" }}>
      <div style={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <div className={errors.first_name ? "field-error" : ""}>
          <label>First Name</label>
          <Input
            ref={firstNameRef}
            autoComplete="new-password"
            placeholder="First Name"
            value={first_name}
            onChange={(e) => handleInputChange("first_name", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, lastNameRef)}
            tabIndex={1}
          />
          {errors.first_name && (
            <div style={{ color: "#af6465", marginTop: "5px" }}>
              {errors.first_name}
            </div>
          )}
        </div>
        <div className={errors.last_name ? "field-error" : ""}>
          <label>Last Name</label>
          <Input
            ref={lastNameRef}
            autoComplete="new-password"
            placeholder="Last Name"
            value={lastName}
            onChange={(e) => handleInputChange("last_name", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, emailRef)}
            tabIndex={2}
          />
          {errors.last_name && (
            <div style={{ color: "#af6465", marginTop: "5px" }}>
              {errors.last_name}
            </div>
          )}
        </div>
        <div className={errors.email ? "field-error" : ""}>
          <label>Email</label>
          <Input
            ref={emailRef}
            autoComplete="new-password"
            placeholder="Email"
            value={email}
            onChange={(e) => handleInputChange("email", e.target.value)}
            onKeyDown={(e) => handleKeyPress(e, phoneRef)}
            tabIndex={3}
          />
          {errors.email && (
            <div style={{ color: "#af6465", marginTop: "5px" }}>
              {errors.email}
            </div>
          )}
        </div>
        <div className={errors.phone_number ? "field-error" : ""}>
          <label>Phone</label>
          <InputMask
            mask="(999) 999-9999"
            value={phone}
            onChange={(e) =>
              handleInputChange(
                "phone_number",
                formatPhoneNumber(e.target.value)
              )
            }
            tabIndex={4}
            className="ant-input css-dev-only-do-not-override-1mmediq ant-input-outlined"
          >
            {/* {
              ((inputProps: any) => (
                <Input
                  {...inputProps}
                  ref={phoneRef}
                  autoComplete="new-password"
                  placeholder="Phone"
                />
              )) as unknown as React.ReactNode
            } */}
          </InputMask>
          <div style={{ color: "#af6465", marginTop: "5px" }}>
            {errors["phone_number"]}
          </div>
        </div>

        {/* <div className={errors.phone_number ? "field-error" : ""}>
          <label>Phone</label>
          <Input
            ref={phoneRef}
            autoComplete="new-password"
            placeholder="Phone"
            value={formatPhoneNumber(phone)}
            onChange={(e) => handleInputChange("phone_number", e.target.value)}
            tabIndex={4}
          />
          {errors.phone_number && (
            <div style={{ color: "#af6465", marginTop: "5px" }}>
              {errors.phone_number}
            </div>
          )}
        </div> */}
        <div className={errors.status ? "field-error" : ""}>
          <label>Status</label>
          <Radio.Group value={status} onChange={handleStatusChange}>
            {options.map((option, index) => (
              <Radio
                key={option.value}
                value={option.value}
                tabIndex={5 + index} // Assign tabIndex dynamically
              >
                {option.label}
              </Radio>
            ))}
          </Radio.Group>
        </div>
      </div>
    </div>
  );
};

const ManageUserModal: React.FC<ManageUserModalProps> = ({
  user,
  visible,
  existingEmails,
  existingPhones,
  onClose,
  onSaveUser,
}) => {
  const { rolesData } = useRolesData();
  const [formData, setFormData] = useState<any>({
    first_name: user?.first_name || "",
    last_name: user?.last_name || "",
    email: user?.email || "",
    phone_number: user?.phone_number || "",
    status: user?.status || "",
    roles: user?.roles ? user.roles.map((role: any) => role.id) : [],
  });
  const [errors, setErrors] = useState<Partial<ManageUserFormData>>({});
  const [acknowledgeChecked, setAcknowledgeChecked] = useState(false);

  const isSaveDisabled =
    Object.values(errors).some((error) => error) ||
    (user?.agent && !acknowledgeChecked);

  useEffect(() => {
    if (visible) {
      setFormData({
        first_name: user?.first_name || "",
        last_name: user?.last_name || "",
        email: user?.email || "",
        phone_number: user?.phone_number || "",
        status: user?.status || "",
        roles: user?.roles ? user.roles.map((role: any) => role.id) : [],
      });
    }
  }, [user, visible]);

  const handleFormDataChange = (updatedData: Partial<ManageUserFormData>) => {
    setFormData((prevData: any) => ({ ...prevData, ...updatedData }));
  };

  const handleRoleChange = (selectedRole: Role) => {
    const updatedRoles = formData.roles.includes(selectedRole.id)
      ? formData.roles.filter((roleId: string) => roleId !== selectedRole.id)
      : [...formData.roles, selectedRole.id];
    setFormData((prevData: any) => ({ ...prevData, roles: updatedRoles }));
  };

  const handleRowClick = (record: Role) => {
    const roleId = record.id;
    const updatedRoles = formData.roles.includes(roleId)
      ? formData.roles.filter((roleId: string) => roleId !== roleId)
      : [...formData.roles, roleId];
    setFormData((prevData: any) => ({ ...prevData, roles: updatedRoles }));
  };

  const findUpdatedProperties = (user: any, formData: any) => {
    const updatedProperties: any = {};
    for (const key in formData) {
      if (formData.hasOwnProperty(key) && user.hasOwnProperty(key)) {
        if (Array.isArray(formData[key]) && Array.isArray(user[key])) {
          if (JSON.stringify(formData[key]) !== JSON.stringify(user[key])) {
            updatedProperties[key] = formData[key];
          }
        } else if (formData[key] !== user[key]) {
          updatedProperties[key] = formData[key];
        }
      }
    }
    return updatedProperties;
  };

  const handleSave = () => {
    const existingRoles = user?.roles?.map((role: any) => role.id) || [];
    const newRoles = formData.roles || [];

    const add_roles = newRoles.filter(
      (role: any) => !existingRoles.includes(role)
    );
    const remove_roles = existingRoles.filter(
      (role: any) => !newRoles.includes(role)
    );

    const updatedProperties = findUpdatedProperties(user, formData);
    delete updatedProperties.roles;

    updatedProperties.add_roles = add_roles;
    updatedProperties.remove_roles = remove_roles;

    onSaveUser(updatedProperties);
  };

  const content = (role: Role) => (
    <div>
      <h3>{role.name}</h3>
      {role.role_permissions.length === 0 ? (
        <p>No permissions available</p>
      ) : (
        <ul>
          {role.role_permissions.map((permission) => (
            <li key={permission.id}>
              <strong>{permission.description}:</strong> {permission.notes}
            </li>
          ))}
        </ul>
      )}
    </div>
  );

  const columns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string, record: Role) => (
        <Popover
          style={{ background: "white" }}
          content={() => content(record)}
          title=""
          placement="left"
        >
          <span>{record.name}</span>
        </Popover>
      ),
    },
    {
      title: "Select",
      key: "select",
      render: (role: Role) => (
        <Checkbox
          checked={formData.roles.includes(role.id)}
          onChange={() => handleRoleChange(role)}
        />
      ),
    },
  ];

  const filteredEmails = existingEmails.filter(
    (email) => email !== user?.email
  );
  const filteredPhones = existingPhones.filter(
    (phone) => phone !== user?.phone_number
  );

  return (
    <Modal
      title={
        <Header
          header="Manage User"
          subheader="View or manage user details, assigned roles, and permissions."
        />
      }
      open={visible}
      footer={null}
      width={800}
      closeIcon={null}
      className="no-padding-modal"
      onCancel={onClose}
    >
      <div style={{ padding: "10px" }}>
        <h3>Step 1: Modify User Details</h3>
        <p>
          Modify basic details of the new user.{" "}
          <small>Email address and phone number should be unique.</small>
        </p>
        <ManageUserForm
          formData={formData}
          existingEmails={filteredEmails}
          existingPhones={filteredPhones}
          onFormDataChange={handleFormDataChange}
          errors={errors}
          setErrors={setErrors}
        />
        <div className="form-groupa" style={{ marginTop: "40px" }}>
          <h3>Step 2: Modify the security roles</h3>
          <p>
            Remember, updating your role will be effective as soon as you save.
            Roles determines what areas and actions they will have access to.
          </p>
          <Alert
            message="Please select at least one role. Hover on the role name to see the permissions associated with the role"
            type="info"
            showIcon
            closable
          />

          <Table
            rowKey="id"
            size="middle"
            columns={columns}
            dataSource={rolesData}
            pagination={false}
            onRow={(record) => ({
              onClick: () => handleRowClick(record),
            })}
          />
        </div>
        {user?.agent && (
          <div>
            <h3>Step 3: Acknowledge changes to connected Agent</h3>
            <p>
              The agent record connected to the user will be modifed based on
              the chagne made to the user record.
            </p>
            <Checkbox onChange={(e) => setAcknowledgeChecked(e.target.checked)}>
              <b>Acknowledged</b>
            </Checkbox>
          </div>
        )}
        <div
          style={{
            display: "flex",
            marginTop: "20px",
            gap: "5px",
          }}
        >
          <Button
            key="cancel"
            onClick={onClose}
            style={{ marginRight: "10px" }}
            tabIndex={7}
          >
            Cancel
          </Button>
          <Button
            key="save"
            type="primary"
            onClick={handleSave}
            tabIndex={6}
            disabled={isSaveDisabled}
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ManageUserModal;
